/**
 * WHAT EACH TUTORIAL TRIGGER POINTS AT. One owner, and a gate that refuses an unmapped one.
 *
 * 🚨 **THE TUTORIAL IS 53 AUTHORED STEPS THAT NO WEB PLAYER HAS EVER SEEN.** `Tutorial/GetConfig`,
 * `GetProgress` and `PostProgress` are all served and **no file under `client-web/src` mentioned
 * any of them** (`tools/verify_route_coverage.py`). A new player arrives at a card game with no
 * explanation of a resource, a mulligan, a turn or an attack.
 *
 * ⭐ **A TRIGGER IS ONE OF TWO THINGS, AND CONFLATING THEM IS THE TRAP.** The original's steps carry
 * `KeywordTriggers` that mix:
 *
 *   · an ANCHOR  - a thing on screen to point at   (`GameHandSelf`, `GameLava_Self`, `GameTimer`)
 *   · an EVENT   - a signal that advances the chain (`EffectPlay`, `BotContinue`, `ReadyToMulligan`)
 *
 * Treating an event as an anchor makes the tutorial hunt for an element that will never exist and
 * stall forever; treating an anchor as an event advances past the thing it was explaining. They are
 * separated here, deliberately and by hand, because only a human reading the step text can tell
 * which is which.
 *
 * ⚠️ **A SELECTOR HERE IS A CLAIM ABOUT THE DOM, AND CLAIMS ROT.** `tools/verify_tutorial_web.py`
 * resolves every anchor against the real screen and fails on one that finds nothing - so a renamed
 * class breaks the BUILD rather than silently un-teaching a step.
 *
 * ⛔ **AN UNMAPPED TRIGGER IS A FAILURE, NOT A DEFAULT.** Falling back to "no anchor" for anything
 * unrecognised is how a step quietly stops pointing at anything while still passing. Every trigger
 * the server ships must appear below, even if the entry says `skip`.
 */

export interface TriggerSpec {
  /** A CSS selector for the thing to spotlight. Absent = this trigger is not an anchor. */
  readonly sel?: string;
  /** This trigger is an engine signal that advances the chain, not something to point at. */
  readonly event?: true;
  /** Deliberately not surfaced. Needs a reason - "we know about that one" must be a fact. */
  readonly skip?: string;
  /** Which screen the anchor lives on. `match` steps are only resolvable during a game. */
  readonly screen?: "match" | "decks" | "menu" | "leagues" | "duels";
}

export const TRIGGERS: Readonly<Record<string, TriggerSpec>> = Object.freeze({
  // ── ENGINE SIGNALS. Not things on a screen. ─────────────────────────────────────────────────
  Start: { event: true },
  StartSession: { event: true },
  ReadyToMulligan: { event: true },
  MulliganFinish: { event: true },
  EffectAdd: { event: true },
  EffectPlay: { event: true },
  BotContinue: { event: true },
  SessionEnd: { event: true },
  ReceiveEndGame: { event: true },
  EndGame: { event: true },
  Welcome: { event: true },
  CardInfo: { event: true },
  GeneralFlow: { event: true },
  HoverCards: { event: true },
  PreviewCard: { event: true },
  SleepingEffect: { event: true },

  // ── THE BOARD ───────────────────────────────────────────────────────────────────────────────
  GameHandSelf: { sel: ".board__hand", screen: "match" },
  GameBoard: { sel: ".board__centre", screen: "match" },
  // ⭐ THE ONE THAT MATTERS MOST. Typed Foxfire is the mechanic that makes this game ours rather
  // than a reskin, and it shipped as five unlabelled coloured pips. Two steps explain it.
  GameLava_Self: { sel: ".foxfire", screen: "match" },
  GameTimer: { sel: ".match__act", screen: "match" },
  HeroHealth_Self: { sel: ".nameplate:not([data-bot])", screen: "match" },
  GameDeck_Self: { sel: ".rail--right", screen: "match" },
  GameGraveyard_Self: { sel: ".rail--left", screen: "match" },
  MulliganRect: { sel: ".tray", screen: "match" },
  MulliganReplace: { sel: ".tray .card", screen: "match" },
  MulliganAccept: { sel: ".match__act", screen: "match" },
  HeroBounds_Opponent: { sel: ".nameplate[data-bot]", screen: "match" },

  // ⚠️ **THESE CARRY A RUNTIME OBJECT ID AND CANNOT BE A FIXED SELECTOR.** `CreatureBounds_Self_42`
  // means "the creature whose runtime id is 42", which the ORIGINAL's scripted tutorial could rely
  // on because it dealt a scripted hand. `cardEl` stamps `data-obj`, so the runner resolves the
  // suffix at run time - the entry below is the SHAPE, and `{id}` is substituted.
  CreatureBounds_Self_42: { sel: '[data-obj="42"]', screen: "match" },
  CreatureAttack_Opponent_44: { sel: '[data-obj="44"] .card__attack', screen: "match" },
  CreatureHealth_Opponent_44: { sel: '[data-obj="44"] .card__health', screen: "match" },
  FullCardBounds_Self_InHand_42: { sel: '.board__hand [data-obj="42"]', screen: "match" },
  FullCardBounds_Self_InChoose_55: { sel: '.tray [data-obj="55"]', screen: "match" },
  FullCardBounds_Self_InChoose_70: { sel: '.tray [data-obj="70"]', screen: "match" },

  // ── THE LOBBY ───────────────────────────────────────────────────────────────────────────────
  // ⚠️ DECKS is a menu LINK, not one of the three plates - it sits in `.menu__links`.
  DecksBtn: { sel: '.menu__links [data-label="DECKS"]', screen: "menu" },
  DuelsBtn: { sel: '.menu__plates [data-label="DUELS"]', screen: "menu" },
  LeaguesBtn: { sel: '.menu__plates [data-label="PLAY"]', screen: "menu" },
  DeckNewBtn: { sel: ".deck__tab--new", screen: "decks" },
  DeckEditBtn: { sel: ".deck__tab--on", screen: "decks" },
  DeckCurrentCards: { sel: ".deck__list", screen: "decks" },
  DeckAvailableCards: { sel: ".deck__collection", screen: "decks" },
  DeckEditMenuExit: { sel: ".deck__sort", screen: "decks" },
  DuelsMenuExit: { sel: ".duel", screen: "duels" },
  LeagueDecks: { sel: ".play__col:nth-child(2)", screen: "leagues" },

  // ── CUT BY DECISION. Each says why; none is a silent default. ───────────────────────────────
  DeckIronsideFlag_1: { skip: "the Ironside/commander layer is cut by decision (GAPS #10)" },
  DeckIronsideChange: { skip: "the Ironside layer is cut by decision (GAPS #10)" },
  FirstIronside: { skip: "the Ironside layer is cut by decision (GAPS #10)" },
  DeckLeagueFlags_1: { skip: "league flags per deck are not a surface we built" },
  LeagueFlags: { skip: "league flags are not a surface we built" },
  LeagueMmr: { skip: "elo is never written (GAPS #7), so there is no MMR to point at" },
  DeckFactionFlag_1: {
    skip: "the deck's loyalty is expressed on every card's own cost badge in our builder rather "
        + "than as one flag, so there is no single element to point at",
  },
  // ⚠️ **I WROTE "the builder has no curve chart" AND IT HAS ONE** (`decks.ts:697`,
  // `.deck__curve`). A skip reason is a CLAIM, and an unchecked claim is how a real surface gets
  // written off. Verified against the code, not remembered.
  DeckLavaCurve: { sel: ".deck__curve", screen: "decks" },
});

/**
 * WHERE EACH CHAIN STARTS. The served steps form THREE chains, not one.
 *
 * ⭐ **A CHAIN IS ENTERED ON ITS HEAD'S TRIGGER AND THEN RUNS BY `NextId`.** Walking `NextId` from
 * the first step alone reaches TWO of fifty-three, because `1_Lobby_Start_Logic` deliberately ends
 * its chain and waits for the match to emit `Welcome`.
 *
 * 🚨 **AND THE 19-STEP LOBBY TOUR WAS UNREACHABLE.** Its head is `59_Lobby_FirstIronside`, entered
 * on `FirstIronside` - and the Ironside layer is CUT by decision, so a trigger that will never fire
 * gated the entire tour of the deck builder, duels and leagues. Every step inside it is correct and
 * none of them could ever run. It is re-entered here at its SECOND step, which is where the tour
 * actually begins, so 18 of the 19 survive the cut instead of none.
 */
export interface Chain {
  /** The engine signal that opens this chain. */
  readonly entry: string;
  /** The step to begin at - not always the chain's head. */
  readonly start: string;
  readonly note?: string;
}

export const CHAINS: readonly Chain[] = Object.freeze([
  { entry: "Start", start: "0_Lobby_Start" },
  { entry: "Welcome", start: "10_Session_Welcome" },
  {
    entry: "LobbyTour",
    start: "60_Lobby_DecksBtn",
    note: "skips 59_Lobby_FirstIronside - the Ironside layer is cut, and its step was the only "
        + "thing standing between a player and the other 18",
  },
]);
